/**
 * Enroute — login return-url round trip
 *
 * Carries a user's original location across the login round trip, so they land
 * back where they started. Two cooperating mechanisms:
 *
 *  1. Shopify's own form fields (`checkout_url` / `return_to`) — honoured
 *     server-side on a normal form login. We backfill them from the query.
 *  2. A sessionStorage fallback — covers flows where Shopify drops the form
 *     field (e.g. the Google social-login round trip).
 *
 * This file is only the page-load behaviour. The URL-building API that callers
 * use (`_erj_.requireLogin` / `loginUrl` / `safeReturnPath` / `clearReturnUrl`)
 * lives in erj-utils.js, which is loaded synchronously and therefore reachable
 * from inline section scripts that run before this deferred file does.
 *
 * Template identity comes from this tag's own data-* attribute rather than
 * sniffing <body class>, so it needs no inline Liquid.
 */
(function () {
  const erj = window._erj_;
  const STORAGE_KEY = erj.RETURN_URL_KEY;

  // Query params accepted as "come back here after login". `return_url` is the
  // canonical one; the other two are read for backwards compatibility with links
  // already in the wild and with Shopify's own field names.
  const RETURN_PARAMS = ["return_url", "return_to", "checkout_url"];

  const isAuthTemplate = document.currentScript?.dataset.authPage === "true";

  /** The return path requested by the current URL's query, if any. */
  function returnPathFromQuery() {
    const params = new URLSearchParams(window.location.search);
    for (const name of RETURN_PARAMS) {
      const path = erj.safeReturnPath(params.get(name));
      if (path) return path;
    }
    return null;
  }

  const currentUrl = window.location.pathname + window.location.search + window.location.hash;

  // Mid-flight through Google's OAuth round trip: leave the stored value alone.
  if (currentUrl.includes("google-callback")) return;

  const isAuthPage = isAuthTemplate || /\/account\/(login|register)\/?$/.test(window.location.pathname);

  if (isAuthPage) {
    // Capture: remember where to return, and hand the same value to Shopify's
    // own form fields.
    const requested = returnPathFromQuery();
    if (!requested) return;

    sessionStorage.setItem(STORAGE_KEY, requested);
    for (const field of document.querySelectorAll('[name="checkout_url"], [name="return_to"]')) {
      field.value = requested;
    }
    return;
  }

  // Restore: we're back on a normal page. If a return target survived the login
  // round trip, honour it once and clear it.
  const storedPath = erj.safeReturnPath(sessionStorage.getItem(STORAGE_KEY));
  erj.clearReturnUrl();
  if (storedPath && currentUrl !== storedPath) {
    window.location.replace(storedPath);
  }
})();
